import { computed, inject, Injectable, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { AppointmentStatusCodeV2, PatientDetailsV2, PmsType, PracticeOperatory, Provider, SiteTreatmentV2 } from '@ffd/api-client';
import { AddTreatmentModalService } from '@libs/charting';
import { DictationSink, VoiceChannelService } from '@libs/voice-input';
import {
  AppointmentReason,
  convertToHexColor,
  createAppointmentActions,
  getReadableTextColor,
  patientActions,
  patientsActions,
  ScheduleCalendarTime,
  selectNewAppointment,
  selectSiteSettingsPmsType,
  selectTeeth,
  TimePattern,
} from '@libs/store';
import { DropdownItem, PrefixData, RequiredInputType, Tooth } from '@libs/ui-lib';
import { DataState, Loadable } from '@libs/utils';
import { Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { NotesScreenService } from '@libs/notes';

/**
 * Prefix data structure for appointment status dropdown items.
 * Contains styling information for colored status badges.
 */
export interface AppointmentStatusPrefixData extends PrefixData {
  /** Hex color code for the badge background */
  backgroundColor: string;
  /** Calculated text color (black or white) for optimal contrast */
  textColor: string;
  /** Short status code to display in the badge */
  statusCode?: string;
}

export interface CreateAppointmentViewModel {
  allOperatoryOptions: DropdownItem<PracticeOperatory>[];
  selectedOperatory?: PracticeOperatory;
  appointmentStatusOptions: DropdownItem<AppointmentStatusCodeV2>[];
  selectedAppointmentStatus?: AppointmentStatusCodeV2;
  completedAppointmentStatus?: AppointmentStatusCodeV2;
  treatments: Loadable<SiteTreatmentV2[]>;
  notes: string;
  selectedProvider?: Provider;
  patient?: PatientDetailsV2;
  reasonOptions: AppointmentReason[];
  selectedReasons: AppointmentReason[];
  isReasonsDataLoading: boolean;
  appointmentDate: DateTime;
  timeBlock: number;
  fromTime: ScheduleCalendarTime;
  toTime: ScheduleCalendarTime;
  appointmentLength: number;
  timePatterns: TimePattern[];
  teeth: Tooth[];
  isTentative?: boolean;
  isDirty?: boolean;
}

@Injectable()
export class CreateAppointmentService {
  private readonly store = inject(Store);
  private readonly addTreatmentModalService = inject(AddTreatmentModalService);
  private readonly noteService = inject(NotesScreenService);
  private readonly voiceChannel = inject(VoiceChannelService);

  public readonly isOpenDental = computed(() => {
    const pmsType = this.store.selectSignal(selectSiteSettingsPmsType)();
    return pmsType === PmsType.OPEN_DENTAL;
  });

  public viewModel = computed<CreateAppointmentViewModel>(() => {
    const state = this.store.selectSignal(selectNewAppointment)();
    const teeth = this.store.selectSignal(selectTeeth)()?.teethWithLayers || [];
    const treatments = this.addTreatmentModalService.selectSignal((state) => state)().treatments;

    const treatmentsFilteredForOpenDental = treatments.data?.filter(
      (treatment) => !treatment.ffdRequiredInput || treatment.ffdRequiredInput === RequiredInputType.Mouth,
    );

    const options = this.isOpenDental() ? treatmentsFilteredForOpenDental : treatments.data;

    return {
      ...state,
      appointmentDate: state.appointmentDate,
      patient: state.patient,
      providers: [],
      selectedProvider: state.selectedProvider,
      allOperatoryOptions: state.allOperatories?.map((operatory) => ({ value: operatory, label: operatory.name })) ?? [],
      selectedOperatory: state.operatory,
      completedAppointmentStatus: state.statusOptions.find((status) => {
        return status.description.toLowerCase().includes('completed');
      }),
      treatments,
      appointmentStatusOptions:
        state.statusOptions?.map((status) => {
          const statusColor = status.statusColor;
          if (!statusColor) {
            return {
              value: status,
              label: status.description,
              itemOverviewClass: 'status-dropdown-item',
              prefixData: null,
            };
          }

          const backgroundColor = convertToHexColor(statusColor);
          return {
            value: status,
            label: statusColor.statusName,
            itemOverviewClass: 'status-dropdown-item',
            prefixData: {
              backgroundColor,
              textColor: getReadableTextColor(backgroundColor),
              statusCode: statusColor.statusCode,
            } as AppointmentStatusPrefixData,
          };
        }) ?? [],
      selectedAppointmentStatus: state.selectedStatus,
      notes: state.notes,
      providersAreLoading: false,
      selectedReasons: state.selectedReasons,
      appointmentLength: state.appointmentLength,
      timePatterns: state.timePatterns,
      teeth,
      reasonOptions:
        options?.map((treatment) => ({
          code: treatment.dentalCode,
          name: treatment.ffdShortDescription ?? '',
          abbreviation: treatment.ffdAbbrevDescription ?? '',
        })) ?? [],
      isReasonsDataLoading: treatments.state === DataState.Loading,
    };
  });

  /**
   * Holds the in-flight utterance while recording. Owned here rather than by a provider: the sink below writes it,
   * and this component subscribes to it for the character count and the auto-scroll.
   */
  public readonly transcriptionControl = new FormControl<string | null>('');
  private readonly dictation = new DictationSink(this.voiceChannel, this.transcriptionControl);
  public readonly dictationFinalized$ = this.dictation.finalized$;

  public transcriptionStatus = computed<'idle' | 'loading' | 'recording'>(() => {
    if (this.voiceChannel.isLoading()) {
      return 'loading';
    }

    return this.voiceChannel.isListening() ? 'recording' : 'idle';
  });
  public lastFocusedCursorOffset = signal(0);

  public init(): void {
    // get treatments for reason selector
    this.store.dispatch(createAppointmentActions.onInit());
    this.lastFocusedCursorOffset.set(this.viewModel().notes.length);
  }

  public setOperatory(operatory: PracticeOperatory): void {
    this.store.dispatch(createAppointmentActions.onSetOperatory({ operatory }));
  }

  public setAppointmentStatus(status: AppointmentStatusCodeV2): void {
    this.store.dispatch(createAppointmentActions.onSetStatus({ status }));
  }

  public setId(id: string): void {
    this.store.dispatch(createAppointmentActions.onSetId({ id }));
  }

  public setProvider(provider: Provider): void {
    this.store.dispatch(createAppointmentActions.onSetProvider({ provider }));
  }

  public setPatient(patient: PatientDetailsV2, isInitialPatientSelect?: boolean): void {
    this.store.dispatch(createAppointmentActions.onSetPatient({ patient, isInitialPatientSelect }));
  }

  public loadTeeth(patient: PatientDetailsV2, siteId: string): void {
    this.store.dispatch(patientActions.onLoadTeeth({ patientId: patient.id, siteId, examCount: 5, silentReload: true }));
  }

  public loadVisits(patient: PatientDetailsV2, siteId: string): void {
    this.noteService.setPatientIdAndSiteId({ patientId: patient.id, siteId });
    this.noteService.init();
  }

  public setReasons(reasons: AppointmentReason[]): void {
    this.store.dispatch(createAppointmentActions.onSetReasons({ reasons }));
  }

  public setStartTime(fromTime: ScheduleCalendarTime): void {
    this.store.dispatch(createAppointmentActions.onSetStartTime({ fromTime }));
  }

  public setEndTime(toTime: ScheduleCalendarTime): void {
    this.store.dispatch(createAppointmentActions.onSetEndTime({ toTime }));
  }

  public setAppointmentLength(appointmentLength: number): void {
    this.store.dispatch(createAppointmentActions.onSetAppointmentLength({ appointmentLength }));
  }

  public setTimePatterns(timePatterns: TimePattern[]): void {
    this.store.dispatch(createAppointmentActions.onSetTimePatterns({ timePatterns }));
  }

  public setAppointmentDate(appointmentDate: DateTime): void {
    this.store.dispatch(createAppointmentActions.onSetAppointmentDate({ appointmentDate }));
  }

  public clearPatientSearchTerm(): void {
    this.store.dispatch(patientsActions.onClearSearchTerm());
  }

  /**
   * `notes` is only meaningful on the START branch below: it is a snapshot of mainContent's current DOM, which may
   * hold manual edits made before recording began and not yet reflected in state.notes. On STOP this must not
   * write state.notes at all - mainContent is frozen for the whole recording session, so the same DOM read would
   * overwrite the just-dictated words with the pre-recording text. Stop-time (and mid-dictation mic-switch)
   * reconciliation is handled separately by commitDictatedText, fed from VoiceTypingTextFieldComponent's
   * `(recordingStopped)`/`(recordingLoading)` outputs - see CreateAppointmentComponent.onRecordingStopped /
   * onRecordingLoading.
   */
  public toggleSpeechRecognitionListening(notes: string): void {
    if (this.voiceChannel.isListening() || this.voiceChannel.isLoading()) {
      this.voiceChannel.close();

      return;
    }

    this.store.dispatch(createAppointmentActions.onSetNotes({ notes }));
    // The control survives the previous session, so clear it or the next utterance appends to the last one.
    this.transcriptionControl.setValue('', { emitEvent: false });
    this.voiceChannel.open();
  }

  /**
   * Writes the correctly-merged dictated text into state.notes. Used for both the mid-dictation mic-switch
   * (`recordingLoading`) and end-of-dictation (`recordingStopped`) cases - unlike the other VoiceTypingTextField
   * consumers, this surface has no debounce/history-stack/autosave to gate, so there is no behavioural difference
   * between the two call sites beyond the guard below, which just avoids a no-op dispatch (and the wholesale
   * mainContent rebuild that a state.notes write triggers) when nothing actually changed, e.g. toggling dictation
   * on and immediately off without speaking.
   */
  public commitDictatedText(notes: string): void {
    if (notes === this.viewModel().notes) {
      return;
    }

    this.store.dispatch(createAppointmentActions.onSetNotes({ notes }));
  }

  public updateLastFocusedCursorOffset(offset: number): void {
    this.lastFocusedCursorOffset.set(offset);
  }
}
